/**
 * PHASE 7 — CPU-side vertex data for the sky layers (pure, unit-tested).
 *
 * Buffers are built once from the catalog (J2000 RA/Dec -> equatorial xyz)
 * and uploaded; the per-frame rotations happen entirely in the shader via the
 * matrices from skyFrame.ts.
 */

import { starCatalog, constellationSegments, constellationIds, deepSkyObjects } from "../catalog/catalog";
import { equatorialVector } from "./skyFrame";

interface SkyPoint {
  raHours: number;
  decDeg: number;
  mag: number;
}

/**
 * Bright-star point data: interleaved [x, y, z, sizePx] per star.
 * Size is derived from magnitude (brighter = larger sprite).
 */
export function buildStarPointData(limitMagnitude = 6.5, baseSizePx = 1.6, sizePerMag = 1.2): Float32Array {
  const points = starPoints(limitMagnitude);
  const buf = new Float32Array(points.length * 4);
  for (let i = 0; i < points.length; i++) {
    const v = equatorialVector(points[i]!.raHours, points[i]!.decDeg);
    const strength = Math.min(1, Math.max(0, (limitMagnitude - points[i]!.mag) / limitMagnitude));
    buf[i * 4] = v[0];
    buf[i * 4 + 1] = v[1];
    buf[i * 4 + 2] = v[2];
    buf[i * 4 + 3] = baseSizePx + sizePerMag * strength;
  }
  return buf;
}

/**
 * Star colours as RGB (blackbody approximation from the catalog B-V index),
 * one vec3 per star, ordered identically to buildStarPointData.
 */
export function buildStarColorData(limitMagnitude = 6.5): Float32Array {
  const buf = new Float32Array(starCount(limitMagnitude) * 3);
  let o = 0;
  for (const star of starCatalog.brightStars(limitMagnitude)) {
    const rgb = blackbodyRgb(star.colorIndex ?? 0.6);
    buf[o++] = rgb[0];
    buf[o++] = rgb[1];
    buf[o++] = rgb[2];
  }
  return buf;
}

/** Number of stars that will be drawn for a magnitude limit. */
export function starCount(limitMagnitude = 6.5): number {
  let n = 0;
  for (const star of starCatalog.brightStars(limitMagnitude)) {
    void star;
    n++;
  }
  return n;
}

function starPoints(limitMagnitude: number): SkyPoint[] {
  const out: SkyPoint[] = [];
  for (const star of starCatalog.brightStars(limitMagnitude)) {
    out.push({ raHours: star.raHours, decDeg: star.decDegrees, mag: star.magnitude });
  }
  return out;
}

/**
 * Constellation line data: one pair of [x, y, z] vertices per segment
 * (GL primitive LINES), all constellations concatenated.
 */
export function buildLinePointData(): Float32Array {
  const segments: { raHours: number; decDeg: number; raHours2: number; decDeg2: number }[] = [];
  for (const id of constellationIds()) {
    for (const [a, b] of constellationSegments(id)) {
      const s0 = starCatalog.star(a);
      const s1 = starCatalog.star(b);
      segments.push({ raHours: s0.raHours, decDeg: s0.decDegrees, raHours2: s1.raHours, decDeg2: s1.decDegrees });
    }
  }
  const buf = new Float32Array(segments.length * 6);
  for (let i = 0; i < segments.length; i++) {
    const v0 = equatorialVector(segments[i]!.raHours, segments[i]!.decDeg);
    const v1 = equatorialVector(segments[i]!.raHours2, segments[i]!.decDeg2);
    buf[i * 6] = v0[0];
    buf[i * 6 + 1] = v0[1];
    buf[i * 6 + 2] = v0[2];
    buf[i * 6 + 3] = v1[0];
    buf[i * 6 + 4] = v1[1];
    buf[i * 6 + 5] = v1[2];
  }
  return buf;
}

/** Number of line vertices in buildLinePointData. */
export function lineVertexCount(): number {
  let n = 0;
  for (const id of constellationIds()) n += constellationSegments(id).length * 2;
  return n;
}

/**
 * Deep-sky object point data: interleaved [x, y, z, sizePx] per object.
 * Size scales gently with the object's physical extent.
 */
export function buildDeepSkyPointData(): Float32Array {
  const buf = new Float32Array(deepSkyObjects.length * 4);
  for (let i = 0; i < deepSkyObjects.length; i++) {
    const dso = deepSkyObjects[i]!;
    const v = equatorialVector(dso.ra / 15, dso.dec);
    buf[i * 4] = v[0];
    buf[i * 4 + 1] = v[1];
    buf[i * 4 + 2] = v[2];
    buf[i * 4 + 3] = 7 + Math.min(6, Math.sqrt(Math.max(0.5, dso.sizeArcmin)));
  }
  return buf;
}

/** B-V index -> approximate RGB (blackbody locus fit). */
export function blackbodyRgb(bv: number): [number, number, number] {
  const t = bv;
  let r: number;
  let g: number;
  let b: number;
  if (t < 0) {
    r = 0.72;
    g = 0.82;
    b = 1.0;
  } else if (t > 2) {
    r = 1.0;
    g = 0.62;
    b = 0.35;
  } else {
    r = 0.9 + 0.05 * t;
    g = 0.95 - 0.2 * t;
    b = 1.0 - 0.32 * t;
  }
  const scale = 1.15 / Math.max(r, g, b);
  return [r * scale, g * scale, b * scale];
}